import * as XLSX from "xlsx";
import dayjs from "dayjs";

const pickValue = (obj, keys) => {
    for (const key of keys) {
        const value = obj?.[key];
        if (value !== undefined && value !== null && String(value).trim() !== "") {
            return String(value).trim();
        }
    }
    return "";
};

export const normalizeErrorRows = (rows = []) => {
    return rows.map((row) => {
        if (typeof row === "string") {
            return {
                Row: "N/A",
                Error: row
            };
        }

        if (!row || typeof row !== "object") {
            return {
                Row: "N/A",
                Error: "Unknown error"
            };
        }

        const errorMsg = pickValue(row, ["Error", "error", "Message", "message", "Reason", "reason", "Status"]) || "Unknown error";
        const rowVal = pickValue(row, ["Row", "row", "rowNum", "RowNum", "RowNumber", "rowNumber"]) || "N/A";

        const rowData = { ...row };
        const knownKeys = ["Row", "row", "rowNum", "RowNum", "RowNumber", "rowNumber", "Error", "error", "Message", "message", "Reason", "reason", "Status"];
        knownKeys.forEach(key => delete rowData[key]);


        return {
            Row: rowVal,
            ...rowData,
            Error: errorMsg
        };
    });
};

export const validateAndParseEmployeeFile = async (fileObj, selectedCorp, depts, jobs, countries, states, relations) => {
    try {
        const buffer = await fileObj.arrayBuffer();
        const workbook = XLSX.read(buffer, { cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rawData = XLSX.utils.sheet_to_json(worksheet, { defval: "", /*raw: false*/ dateNF: "mm/dd/yyyy" });

        const jsonData = rawData.map((row) => {
            const trimmedRow = {};
            Object.keys(row).forEach((key) => {
                const value = row[key];
                trimmedRow[key.trim()] = typeof value === 'string' ? value.trim() : value;
            });
            return trimmedRow;
        });

        if (jsonData.length === 0) {
            return { error: "The uploaded file is empty." };
        }

        // Header Validation
        const expectedHeaders = [
            "Salutation", "First Name", "Middle Name", "Last Name", "SSN", "Gender", 
            "Date Of Birth", "Marital Status", "Mobile Num.", "Email ID", "Work Num.", 
            "Alternate Num.", "Fax Num.", "Pay Frequency", "Department", "JobTitle", 
            "Address", "City", "Country", "State", "Zip Code", "Emergency Name", 
            "Emergency Relation", "Emergency Mobile Num.", "Emergency Email ID", "Status"
        ];
        const actualHeaders = Object.keys(jsonData[0] || {}).map(h => h.trim().toLowerCase()).filter(h => !h.includes('__empty'));
        const expectedLower = expectedHeaders.map(h => h.toLowerCase());

        if (JSON.stringify(actualHeaders.sort()) !== JSON.stringify(expectedLower.sort())) {
            return { errorRows: normalizeErrorRows([{ Row: "Header", Error: "Column headers do not match the template. Please use the correct format." }]) };
        }

        const cleanPhone = (val) => {
            if (!val) return null;
            return String(val).replace(/[^0-9]/g, '').slice(0, 15) || null;
        };

        const formatSSN = (val) => {
            if (!val) return null;
            const clean = String(val).replace(/[^0-9]/g, '');
            if (clean.length === 9) {
                return `${clean.slice(0, 3)}-${clean.slice(3, 5)}-${clean.slice(5)}`;
            }
            return String(val);
        };

        const formatExcelDate = (val) => {
            if (!val) return null;

            // Already a Date object
            if (val instanceof Date) {
                return dayjs(val).format("MM/DD/YYYY");
            }

            // Excel serial number
            if (typeof val === "number") {
                return dayjs(XLSX.SSF.parse_date_code(val)).format("MM/DD/YYYY");
            }

            // String
            const d = dayjs(val);

            return d.isValid() ? d.format("MM/DD/YYYY") : null;
        };

        const empImportData = jsonData.map((row) => ({
            Salutation: row["Salutation"] || row["salutation"] || null,
            FirstName: row["First Name"] || row["FirstName"] || "",
            MiddleName: row["Middle Name"] || row["MiddleName"] || null,
            LastName: row["Last Name"] || row["LastName"] || null,
            SSN: row["SSN"] ? formatSSN(row["SSN"]) : null,
            Gender: row["Gender"] || null,
            DOB: formatExcelDate(row["Date Of Birth"] || row["Date of Birth"] || row["DOB"] || null),
            MaritalStatus: row["Marital Status"] || row["MaritalStatus"] || null,
            MobileNum: cleanPhone(row["Mobile Num."] || row["MobileNum"] || row["Mobile Number"] || null),
            EmailID: row["Email ID"] || row["EmailID"] || null,
            WorkNum: cleanPhone(row["Work Number"] || row["WorkNum"] || row["Work Num."] || null),
            AlterNativeNum: cleanPhone(row["Alternative Number"] || row["AlterNativeNum"] || row["Alternate Num."] || null),
            FaxNum: cleanPhone(row["Fax Number"] || row["FaxNum"] || row["Fax Num."] || null),
            PayFrequency: row["Pay Frequency"] || row["PayFrequency"],
            Department: row["Department"] || null,
            JobTitle: row["Job Title"] || row["JobTitle"] || null,
            Address: row["Address"] || null,
            City: row["City"] || row["city"] || null,
            Country: row["Country"] || null,
            State: row["State"] || row["state"] || null,
            ZipCode: cleanPhone(row["Zip Code"] || row["ZipCode"]) ? String(row["Zip Code"] || row["ZipCode"]) : null,
            EmergencyName: row["Emergency Name"] || row["EmergencyName"] || null,
            EmergencyRelation: row["Emergency Relation"] || row["EmergencyRelation"] || null,
            EmergencyMobileNum: cleanPhone(row["Emergency Mobile Number"] || row["EmergencyMobileNum"] || row["Emergency Mobile Num."] || null),
            EmergencyEmailID: row["Emergency Email ID"] || row["EmergencyEmailID"] || null,
            Status: row["Status"] || null
        }));

        const payload = {
            CorporationID: selectedCorp.corpValue,
            EmpImportData: empImportData
        };
        const allRows = jsonData.map((row, index) => ({
            __rowNum__: index + 2, // Excel row number (header is row 1)
            ...row
        }));

        return { payload, allRows, totalRecords: jsonData.length, validRecords: jsonData.length };

    } catch (err) {
        console.error("Validation error", err);
        return { error: "Import failed. Please verify your file." };
    }
};
